import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { AuthStore } from "stores/AuthStore";
import { countsTowardChartLimit } from "./chartLimit";
import DashboardLayoutStore from "./Layout/DashboardLayoutStore";
import {
    ApiLayoutPersistence,
    snapshotFromResponse,
} from "./Layout/persistence";
import type { LayoutMerge } from "./Layout/persistence";
import type { LayoutItem, LayoutObject } from "./Layout/types";
import { planCardSync } from "./cardSync";
import _ from "lodash";
import {
    DashboardTypeRoutes,
    IDashboardTenantVisibility,
    ISignalsDashboard,
    ISignalsTextCard,
    RenderedSignalsObject,
    SignalsDashboardType,
    SignalsObjectType,
    StandardDashboardTypes,
    StandardHomes,
} from "../SignalsModels";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
    when,
    type IReactionDisposer,
} from "mobx";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import { ServiceError } from "services/BaseService";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import AsyncQueue from "../BaseObjects/AsyncQueue";
import BaseComponentStore, {
    type RefreshResult,
} from "../BaseObjects/BaseObjectStore";
import { createComponentStoreFromBaseObject } from "../BaseObjects/utils";
import { AbortError } from "@microsoft/signalr";
import SignalsCardSelectorStore from "./AddCards/SignalsCardSelectorStore";
import {
    ImmediateDashboardEmailObjectOverridesById,
    SelectedCardRequestPayload,
    SignalsService,
} from "services/SignalsService";
import { isDevelopmentEnvironment } from "utils/EnvironmentUtils";
import { filterByPerms } from "../SignalsUtils";
import FilesService from "services/FilesService";
import { ObjectSelectableFilterKey } from "../BaseObjects/ObjectFilters";
import ChartCardStore from "../BaseObjects/ChartCard/ChartCardStore";

export type ObjectFilterConflict = {
    componentId: string;
    componentTitle: string;
    filterKey: ObjectSelectableFilterKey;
    conflictingValues: unknown[];
};

type LogoBlobInfo = { blobSasUrl: string; fileName: string };
// SIGNALSTODO - we may want to implement this into the org store so we can set the limit per org
// For now we are limiting to 3 during testing, but we might want to drop to 1 when going live and
// manually test larger tenants to see what a good default is.
const MAX_CONCURRENT_REFRESHES = 3;
const MAX_DEFER_REFRESH_PASSES = 6;
const DEFER_REFRESH_WAIT_TIMEOUT_MS = 20000;
const DEFER_REFRESH_WAIT_POLL_MS = 100;
const EXPORT_STAGE_TIMEOUT_MS = 60000;
const EXPORT_CHART_SETTLE_MS = 1250;

/**
 * Store for managing the state and actions related to an individual Signals Dashboard. This has all of the properties that exist on a dashboard as well as observable values.
 * It also includes methods for updating the dashboard, managing user permissions, and handling visibility settings.
 */
class DashboardStore extends BaseStore {
    private readonly signalsService: SignalsService;
    private readonly filesService: FilesService;
    private readonly messageStore: MessageStore;
    private readonly applicationFiltersStore: ApplicationFiltersStore;
    private readonly signalsCardSelectorStore: SignalsCardSelectorStore;
    private readonly rootStore: IRootStore;
    /** Owns this dashboard's card arrangement. */
    readonly dashboardLayoutStore: DashboardLayoutStore;
    private readonly reactionDisposers: IReactionDisposer[] = [];

    // Dashboard properties
    id: string;
    @observable signalsObjects: RenderedSignalsObject[];
    @observable signalsChartCount: number;
    @observable title: string;
    @observable description: string;
    @observable userVisibility?: ReportAccessType; // We use ReportAccessType as the type for UserVisibility
    @observable tenantVisibility?: IDashboardTenantVisibility;
    @observable userId?: string;
    @observable logoName: string;
    @observable pinnedApplicationFilterId?: string;
    @observable signalsDashboardSubscriptionId?: string;
    @observable createdOn: any;
    @observable dashboardType: SignalsDashboardType;
    /**
     * Layout items for cards this user cannot see. The server sends their
     * place but nothing else, and the grid draws a placeholder there.
     */
    @observable.ref private hiddenLayoutItems: LayoutItem[] = [];

    // Store properties - these are not in the database but are useful for UI state
    @observable isFavorite: boolean = false;
    @observable logo: LogoBlobInfo | undefined;
    @observable isLogoLoading: boolean = false;
    @observable isLogoSourceMissing: boolean = false;
    @observable isUserHomeDashboard: boolean = false;
    @observable isOrgHomeDashboard: boolean = false;
    @observable isPresentationMode: boolean = false;
    @observable isSelected: boolean = false;
    private logoObjectUrl: string | undefined;
    // Dashboard-wide baseline of filters that most recently completed apply/refresh.
    // Used for dashboard-level restore/cancel behavior and as the source passed to objects during apply.
    // We also use this to determine if a dashboard needs refreshed since the filters have changed from the last applied state.
    @observable.ref lastAppliedFilters: ApplicationFilters | null = null;
    @observable.ref
    private pendingAppliedFiltersForRefresh: ApplicationFilters | null = null;
    @observable objectFilterConflicts: ObjectFilterConflict[] = [];
    @observable isObjectFilterConflictDialogOpen: boolean = false;

    // Component things
    @observable components: BaseComponentStore<any, any>[] = [];
    private refreshQueue: AsyncQueue | null = null;
    private pendingRefreshComponents: Set<BaseComponentStore<any, any>> =
        new Set();
    @observable private _exportCancelled = false;
    @observable isExportCaptureInProgress = false;

    // Viewport-aware lazy loading
    private visibleComponentIds: Set<string> = new Set();
    private lazyRefreshGeneration = 0;
    /** Identifies the latest `syncFromServer`, so an older response is dropped. */
    private syncGeneration = 0;
    private isDisposed = false;
    private lazyPendingMap: Map<string, BaseComponentStore<any, any>> =
        new Map();
    private resolveInitialVisibility: (() => void) | null = null;
    private initialVisibilityPromise: Promise<void> = new Promise<void>(
        (resolve) => {
            this.resolveInitialVisibility = resolve;
        },
    );

    public constructor(dashboard: ISignalsDashboard, rootStore: IRootStore) {
        super("Dashboard Store");
        makeObservable(this);
        // Initialize dashboard properties from the passed in dashboard object we got from the API
        this.id = dashboard.id;
        this.signalsObjects = dashboard.signalsObjects ?? [];
        this.hiddenLayoutItems = this.findHiddenLayoutItems(
            dashboard.layout ?? [],
        );
        this.signalsChartCount = dashboard.signalsChartCount ?? 0;
        this.title = dashboard.title ?? "";
        this.description = dashboard.description ?? "";
        this.userVisibility = dashboard.userVisibility;
        this.tenantVisibility = dashboard.tenantVisibility;
        this.userId = dashboard.userId;
        this.logoName = dashboard.logoName ?? "";
        this.pinnedApplicationFilterId = dashboard.pinnedApplicationFilterId;
        this.signalsDashboardSubscriptionId =
            dashboard.signalsDashboardSubscriptionId;
        this.createdOn = dashboard.createdOn;
        this.dashboardType = dashboard.dashboardType;

        this.signalsService = new SignalsService();
        this.filesService = new FilesService();
        this.messageStore = rootStore.getStore(MessageStore);
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        // Keep using the V2 selector store even when the V1 dialog is shown so multi-add logic remains available.
        this.signalsCardSelectorStore = rootStore.getStore(
            SignalsCardSelectorStore,
        );
        this.rootStore = rootStore;

        this.logo = undefined;
        this.isLogoLoading = false;
        this.isLogoSourceMissing = false;
        this.logoObjectUrl = undefined;

        // Reaction to fetch logo whenever logoName changes
        const disposeLogoReaction = reaction(
            () => this.logoName,
            () => {
                if (!this.logoName) {
                    runInAction(() => {
                        this.revokeLogoObjectUrl();
                        this.logo = undefined;
                        this.isLogoLoading = false;
                        this.isLogoSourceMissing = false;
                    });
                    return;
                }

                // Whenever the logoName changes, refetch the logo bytes via FilesController.
                void this.fetchLogoForDashboard();
            },
            { fireImmediately: true },
        );
        this.reactionDisposers.push(disposeLogoReaction);

        // Setup components in the dashboard
        this.signalsObjects
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .forEach((object) => {
                this.components.push(this.createComponentStore(object));
            });

        // Built after the components, because a dashboard without a stored
        // layout is seeded from their order.
        this.dashboardLayoutStore = new DashboardLayoutStore({
            dashboardId: this.id,
            objects: this.layoutObjects,
            initial: dashboard.layout
                ? snapshotFromResponse({
                      items: dashboard.layout,
                      schemaVersion: dashboard.layoutSchemaVersion,
                  })
                : null,
            persistence: new ApiLayoutPersistence(this.signalsService),
            onMerged: this.applyMergedLayoutSave,
            onCardOrders: this.applyCardOrders,
        });

        // Keep the layout in step as cards are added or removed.
        this.reactionDisposers.push(
            reaction(
                () => this.layoutObjects,
                (objects) =>
                    this.dashboardLayoutStore.reconcileObjects(objects),
            ),
        );
    }

    /** Stops this store's reactions once it is replaced or removed. */
    dispose = () => {
        this.isDisposed = true;
        this.reactionDisposers.forEach((dispose) => dispose());
        this.reactionDisposers.length = 0;
    };

    private revokeLogoObjectUrl() {
        if (this.logoObjectUrl) {
            URL.revokeObjectURL(this.logoObjectUrl);
            this.logoObjectUrl = undefined;
        }
    }

    private async fetchLogoForDashboard() {
        runInAction(() => {
            this.isLogoLoading = true;
            this.isLogoSourceMissing = false;
        });

        try {
            const logoBlob = await this.filesService.downloadDashboardLogo(
                this.id,
            );
            const nextObjectUrl = URL.createObjectURL(logoBlob);

            runInAction(() => {
                this.revokeLogoObjectUrl();
                this.logoObjectUrl = nextObjectUrl;
                this.logo = {
                    blobSasUrl: nextObjectUrl,
                    fileName: this.logoName,
                };
                this.isLogoLoading = false;
                this.isLogoSourceMissing = false;
            });
        } catch {
            runInAction(() => {
                this.revokeLogoObjectUrl();
                this.logo = undefined;
                this.isLogoLoading = false;
                this.isLogoSourceMissing = true;
            });
        }
    }

    // Standard home dashboards are ones that match the standard home title for their type
    // For example, Themes and Drivers is the standard home for ThemesAndDrivers dashboard type and lives under the /drivers route
    @computed get isStandardHomeDashboard() {
        if (
            this.isStandardDashboard &&
            this.title === StandardHomes.get(this.dashboardType)
        ) {
            return true;
        } else {
            return false;
        }
    }

    public getImmediateEmailObjectOverrides():
        | ImmediateDashboardEmailObjectOverridesById
        | undefined {
        const overridesById: ImmediateDashboardEmailObjectOverridesById = {};

        this.components.forEach((component) => {
            if (!component.isEditable || component.isSaveable) {
                return;
            }

            const objectFilters =
                component.getSerializedObjectFiltersForPersistence();
            const vizOptions = component.getEffectiveVizOptionsForPersistence();

            if (objectFilters === undefined && vizOptions === undefined) {
                return;
            }

            overridesById[component.id] = {
                objectFilters,
                vizOptions,
            };
        });

        return Object.keys(overridesById).length > 0
            ? overridesById
            : undefined;
    }

    @computed get isStandardDashboard() {
        return StandardDashboardTypes.includes(this.dashboardType);
    }

    @computed
    get dashboardIsEditable() {
        return (
            this.userVisibility === ReportAccessType.Edit ||
            this.userVisibility === ReportAccessType.Owner
        );
    }

    /** Whether the layout is being arranged. */
    @computed
    get isArranging(): boolean {
        return this.dashboardLayoutStore.isEditing;
    }

    /** The cards the layout needs to know about, and nothing else. */
    @computed
    get layoutObjects(): LayoutObject[] {
        const cards = this.components.map((component) => ({
            id: component.id,
            objectType: component.objectType,
            chartType:
                component instanceof ChartCardStore
                    ? component.chartType
                    : undefined,
            dataEndpoint: component.dataEndpoint,
            order: component.order,
        }));
        const placeholders = this.hiddenLayoutItems.map((item) => ({
            id: item.objectId,
            objectType: SignalsObjectType.Chart,
            order: Number.MAX_SAFE_INTEGER,
            placeholderSize: { width: item.width, height: item.height },
        }));
        return [...cards, ...placeholders];
    }

    /** Whether an id in the layout is a card this user cannot see. */
    isHiddenCard = (objectId: string) =>
        this.hiddenLayoutItems.some((item) => item.objectId === objectId);

    private findHiddenLayoutItems(items: readonly LayoutItem[]): LayoutItem[] {
        const visibleIds = new Set(this.signalsObjects.map((obj) => obj.id));
        return items.filter((item) => !visibleIds.has(item.objectId));
    }

    /**
     * Brings this dashboard into step with the server after its cards changed,
     * here or by someone else. Every card still on the dashboard keeps its
     * store, so its zoom, %/# mode, compare-previous and selections survive;
     * new cards get stores and load; removed cards' stores go. The layout and
     * every card's order come from the server. Cards someone else
     * reconfigured keep this page's definition until the dashboard reloads.
     * Only the latest sync applies, and its layout only when no save has
     * committed since it asked: an earlier sync can answer last.
     */
    syncFromServer = async (): Promise<void> => {
        const generation = ++this.syncGeneration;
        const savesBeforeSync = this.dashboardLayoutStore.committedSaveCount;
        const isSuperseded = () =>
            this.isDisposed || generation !== this.syncGeneration;

        let dashboard: ISignalsDashboard | null;
        try {
            dashboard = await this.signalsService.getDashboard(this.id);
        } catch (e) {
            if (isSuperseded()) return;
            console.error("Error syncing dashboard", e);
            this.messageStore.logError(
                "The dashboard could not be updated. Reload the page to see its latest cards.",
            );
            return;
        }
        if (!dashboard || isSuperseded()) return;

        const layoutIsCurrent =
            savesBeforeSync === this.dashboardLayoutStore.committedSaveCount;
        const visibleObjects = filterByPerms(
            dashboard,
            this.rootStore.getStore(AuthStore),
        );
        runInAction(() =>
            this.applyServerCards(
                visibleObjects,
                dashboard.layout ?? [],
                dashboard.signalsChartCount ?? 0,
                layoutIsCurrent,
            ),
        );
    };

    @action
    private applyServerCards(
        serverObjects: RenderedSignalsObject[],
        layoutItems: LayoutItem[],
        signalsChartCount: number,
        layoutIsCurrent: boolean,
    ) {
        const plan = planCardSync(this.signalsObjects, serverObjects);
        const componentsById = new Map(
            this.components.map((component) => [component.id, component]),
        );

        this.signalsObjects = plan.signalsObjects;
        this.components = plan.signalsObjects.map((obj) => {
            const existing = componentsById.get(obj.id);
            if (!existing) return this.createComponentStore(obj, true);

            existing.order = obj.order ?? 0;
            return existing;
        });
        this.hiddenLayoutItems = this.findHiddenLayoutItems(layoutItems);
        // The server's count includes cards this user cannot see, which the
        // chart limit and the hidden-cards banner both rely on.
        this.signalsChartCount = signalsChartCount;

        this.dashboardLayoutStore.reconcileObjects(this.layoutObjects);
        const snapshot = layoutIsCurrent
            ? snapshotFromResponse({ items: layoutItems })
            : null;
        if (snapshot) this.dashboardLayoutStore.applyServerSnapshot(snapshot);

        if (plan.addedIds.length > 0 && !this.isSelected) {
            // Cards added to an inactive dashboard may skip their own refresh.
            // Clear the applied snapshot so navigating back refreshes them.
            this.lastAppliedFilters = null;
        }
    }

    /**
     * A store for one of this dashboard's cards. A card that has just joined
     * the dashboard sets `refreshOnCreate` so it loads straight away.
     */
    private createComponentStore(
        obj: RenderedSignalsObject,
        refreshOnCreate = false,
    ) {
        return createComponentStoreFromBaseObject({
            baseObject: obj,
            rootStore: this.rootStore,
            refreshOnCreate,
            options: {
                // A card's selections, object filters and grouping can be
                // changed on a dashboard the user can edit, and on a standard
                // dashboard, whose cards are editable without permission.
                isEditable: this.dashboardIsEditable || this.isStandardDashboard,
                isDeletable: this.dashboardIsEditable,
                // Whether changes persist. Without it, edits apply only to this
                // session, even where isEditable allows them.
                isSaveable: this.dashboardIsSaveable,
                isDashboardSelected: () => this.isSelected,
                getDashboardAppliedFilters: () =>
                    this.pendingAppliedFiltersForRefresh ??
                    this.lastAppliedFilters,
                static:
                    obj.objectType === SignalsObjectType.MontageCard ||
                    obj.objectType === SignalsObjectType.TextCard,
                isPresentationMode: this.isPresentationMode,
            },
        });
    }

    /**
     * Takes the card orders a layout save derived, so whatever follows order
     * (the narrow view, refresh priority) matches the saved grid.
     */
    private applyCardOrders = (orders: Record<string, number>) => {
        this.components.forEach((component) => {
            const order = orders[component.id];
            if (order !== undefined) component.order = order;
        });
        this.signalsObjects = this.signalsObjects.map((obj) =>
            orders[obj.id] === undefined
                ? obj
                : { ...obj, order: orders[obj.id] },
        );
    };

    /**
     * A layout save added cards someone else added, or dropped cards someone
     * else deleted. Removed cards leave at once, in the save's own action, so
     * they never flash at the bottom of the grid; that includes placeholders
     * for cards this user cannot see. Then the dashboard loads the rest from
     * the server.
     */
    private applyMergedLayoutSave = (merge: LayoutMerge) => {
        const removedIds = new Set(merge.removedObjectIds);
        this.components = this.components.filter(
            (component) => !removedIds.has(component.id),
        );
        this.signalsObjects = this.signalsObjects.filter(
            (obj) => !removedIds.has(obj.id),
        );
        this.hiddenLayoutItems = this.hiddenLayoutItems.filter(
            (item) => !removedIds.has(item.objectId),
        );

        const counts = isDevelopmentEnvironment()
            ? ` (${merge.addedObjectIds.length} cards added, ${merge.removedObjectIds.length} cards removed)`
            : "";
        this.messageStore.logMessage(
            `Dashboard changes by someone else were merged into layout.${counts}`,
            "info",
        );

        void this.syncFromServer();
    };

    /**
     * Cards cannot be added or deleted while the layout is being arranged:
     * the arrangement is saved or canceled first. Logs why and returns true
     * when a change is refused.
     */
    private refuseCardChangeWhileArranging(): boolean {
        if (!this.isArranging) return false;

        this.messageStore.logError(
            "Save or cancel your layout changes before adding or deleting cards.",
        );
        return true;
    }

    /** Whether this user may rearrange the dashboard for everyone who sees it. */
    @computed
    get canEditDashboardLayout() {
        return this.dashboardIsSaveable && !this.isPresentationMode;
    }

    @computed
    get dashboardIsSaveable() {
        return this.dashboardIsEditable && !this.isStandardDashboard;
    }

    @computed
    get charts(): ChartCardStore[] {
        return this.components.filter(
            (component) => component.objectType === SignalsObjectType.Chart,
        ) as ChartCardStore[];
    }

    /** This chartCount includes all Charts, Tables, and Unique cards in the count (since all count toward the signalsChartCount) */
    @computed
    get chartCount() {
        return this.components.filter((component) =>
            countsTowardChartLimit(component.objectType),
        ).length;
    }

    /**
     * Updates the dashboard properties such as title, logo name, and pinned application filter ID.
     * All optional parameters, will only set the ones that are supplied in the object parameter
     * @param title New title for the dashboard
     * @param description New description for the dashboard
     * @param logoName New logo name for the dashboard
     * @param pinnedApplicationFilterId New pinned application filter ID for the dashboard
     */
    @action
    updateDashboard = async ({
        title,
        description,
        logoName,
        pinnedApplicationFilterId,
    }: {
        title: string;
        description?: string;
        logoName: string | undefined;
        pinnedApplicationFilterId: string | undefined;
    }) => {
        try {
            const updateDashRes = await this.signalsService.updateDashboard({
                id: this.id,
                pinnedApplicationFilterId,
                title,
                description,
                logoName,
            });

            this.title = updateDashRes.title ?? this.title;
            this.description = updateDashRes.description ?? this.description;
            this.logoName = updateDashRes.logoName; // ?? this.logoName;
            this.pinnedApplicationFilterId =
                updateDashRes.pinnedApplicationFilterId ??
                this.pinnedApplicationFilterId;

            this.messageStore.logMessage("Dashboard updated", "success");
        } catch (e) {
            if (e instanceof ServiceError && e.errorMessage) {
                this.messageStore.logError(e.errorMessage);
            } else {
                this.messageStore.logMessage(
                    "Failed to update dashboard",
                    "error",
                );
            }
        }
    };

    @action
    toggleCurrentDashboardPinnedFilter = async (savedFilterId?: string) => {
        this.setupAsyncTask("Pin Filter", async () => {
            try {
                const isUnpinning =
                    this.pinnedApplicationFilterId === savedFilterId;

                let newValue: string | undefined = savedFilterId;
                if (isUnpinning) {
                    newValue = undefined;
                }

                await this.updateDashboard({
                    title: this.title,
                    logoName: this.logoName,
                    pinnedApplicationFilterId: newValue,
                });

                if (isUnpinning) {
                    this.messageStore.logMessage(
                        "Filter successfully unpinned from dashboard",
                        "success",
                    );
                    this.pinnedApplicationFilterId = undefined;
                } else {
                    this.messageStore.logMessage(
                        "Filter successfully pinned to dashboard",
                        "success",
                    );
                }
            } catch (e) {
                if (e instanceof ServiceError && e.errorMessage) {
                    this.messageStore.logError(e.errorMessage);
                } else {
                    this.messageStore.logMessage(
                        "Failed to update dashboard",
                        "error",
                    );
                }
            }
        });
    };

    // Dashboard permission methods

    async getDashboardUserPermissions() {
        const userPermissions =
            await this.signalsService.getDashboardUserPermissions(this.id);
        return userPermissions;
    }

    @action
    async updateDashboardVisibility(value: IDashboardTenantVisibility) {
        try {
            await this.signalsService.updateDashboardVisibility(this.id, value);

            this.tenantVisibility = value;

            this.messageStore.logMessage(
                "Dashboard visibility updated.",
                "success",
            );
        } catch (error) {
            if (error instanceof ServiceError && error.errorMessage) {
                this.messageStore.logError(error.errorMessage as string);
            } else {
                this.messageStore.logError(
                    "An unknown error occurred" + JSON.stringify(error),
                );
            }
        }
    }

    @action
    async updateDashboardUserPermissions({
        userIds,
        permissionToSet,
    }: {
        userIds: string[];
        permissionToSet: ReportAccessType;
    }) {
        try {
            const updatedPermissions =
                await this.signalsService.updateDashboardUserPermissions(
                    this.id,
                    userIds as string[],
                    permissionToSet,
                );

            this.messageStore.logMessage(
                `Shared with ${userIds.length} user${userIds.length === 1 ? "" : "s"
                }`,
                "success",
            );
            return updatedPermissions;
        } catch (error) {
            if (error instanceof ServiceError && error.errorMessage) {
                this.messageStore.logError(error.errorMessage as string);
            } else {
                this.messageStore.logError(
                    "An unknown error occurred" + JSON.stringify(error),
                );
            }
            return [];
        }
    }

    async updateSingleUserPermission({
        userId,
        newPermission,
    }: {
        userId: string;
        newPermission: ReportAccessType;
    }) {
        try {
            await this.signalsService.updateDashboardUserPermissions(
                this.id,
                [userId],
                newPermission,
            );
            this.messageStore.logMessage("User permission updated.", "success");
        } catch (error) {
            this.messageStore.logError("Failed to update user permission");
        }
    }

    async removeUserPermission(userId: string) {
        try {
            await this.signalsService.deleteDashboardUserPermission(
                this.id,
                userId,
            );
            this.messageStore.logMessage(
                "User removed from dashboard.",
                "success",
            );
            return true;
        } catch (error) {
            this.messageStore.logError("Failed to remove user permission");
            return false;
        }
    }

    /* ------------- FILTERS -------------- */

    /**  Apply the current filters, update the Signals state, and refresh the dashboard components
     * If all components refresh without an Abort error, update the last applied filters since we successfully applied them
     */
    @action
    applyFilters = async (origin: "manual" | "auto" = "manual") => {
        // Ensure quick filter selections are copied onto the primary filters store
        // so outbound requests reflect the quick filter state.
        this.applicationFiltersStore.syncQuickFiltersIntoMain();

        if (
            this.applicationFiltersStore.selectedSavedFilter &&
            this.applicationFiltersStore.hasChangedSinceLastUpdate
        ) {
            this.applicationFiltersStore.setSelectedSavedFilters();
        }

        const candidateFilters = this.applicationFiltersStore.toFilterObject();
        const objectFilterConflicts =
            this.collectObjectFilterConflicts(candidateFilters);

        if (objectFilterConflicts.length > 0) {
            if (origin === "manual") {
                this.objectFilterConflicts = objectFilterConflicts;
                this.isObjectFilterConflictDialogOpen = true;
            } else {
                this.messageStore.logInfo(
                    "Dashboard filters were not applied due to potential conflicts with object filters. Hit apply to continue applying them.",
                );
            }
            return false;
        }

        this.clearObjectFilterConflicts();
        return await this.applyFiltersCore();
    };

    @action
    closeObjectFilterConflictDialog = () => {
        this.clearObjectFilterConflicts();
    };

    @action
    cancelApplyWithObjectFilterConflicts = () => {
        this.clearObjectFilterConflicts();

        if (!this.lastAppliedFilters) {
            this.messageStore.logInfo(
                "Dashboard filters have not been applied due to conflicts with chart filters.",
            );
            return;
        }

        this.applicationFiltersStore.applyExternalFilters(
            _.cloneDeep(this.lastAppliedFilters),
            true,
        );
    };

    @action
    continueApplyWithObjectFilterConflicts = async () => {
        this.applicationFiltersStore.syncQuickFiltersIntoMain();
        const candidateFilters = this.applicationFiltersStore.toFilterObject();

        // Resolve conflicts into each component's committed object-filter state first
        // so the refresh that follows uses the same clamped payload we persist.
        const changedComponents = this.components.filter((component) =>
            component.removeConflictingCommittedObjectFilterValues(
                candidateFilters,
            ),
        );

        if (changedComponents.length > 0) {
            const persistenceResults = await Promise.all(
                changedComponents.map(async (component) => {
                    const persisted =
                        await component.persistCommittedObjectFilters();
                    if (!persisted) return false;

                    const serializedObjectFilters =
                        component.getSerializedObjectFiltersForPersistence();
                    const matchedObject = this.signalsObjects.find(
                        (obj) => obj.id === component.id,
                    );
                    if (matchedObject) {
                        runInAction(() => {
                            matchedObject.objectFilters =
                                serializedObjectFilters ?? null;
                        });
                    }

                    return true;
                }),
            );

            if (persistenceResults.some((result) => !result)) {
                this.messageStore.logError(
                    "Failed to save some chart filter updates.",
                );
            }
        }

        this.clearObjectFilterConflicts();
        await this.applyFiltersCore();
    };

    private async applyFiltersCore() {
        const candidateFilters = this.applicationFiltersStore.toFilterObject();
        this.applicationFiltersStore.saveCurrentFilterState("Signals");

        // Pass the snapshot into refresh() so it is set AFTER cancelRefresh() runs inside
        // refresh(), avoiding the race where cancelRefresh() would clear values set here.
        await this.refresh(_.cloneDeep(candidateFilters));

        // Return whether the eager phase completed cleanly; lazy cards may still be running.
        return !this.components.some((component) => component.abortError);
    }

    private clearObjectFilterConflicts() {
        this.objectFilterConflicts = [];
        this.isObjectFilterConflictDialogOpen = false;
    }

    private collectObjectFilterConflicts(
        sourceFilters: ApplicationFilters,
    ): ObjectFilterConflict[] {
        const conflicts: ObjectFilterConflict[] = [];

        this.components.forEach((component) => {
            if (!component.supportsObjectFilters()) return;

            const componentConflicts =
                component.getCommittedObjectFilterConflictsAgainstSource(
                    sourceFilters,
                );

            componentConflicts.forEach((conflict) => {
                if (!conflict.conflictingValues?.length) return;
                conflicts.push({
                    componentId: component.id,
                    componentTitle: component.title ?? "Untitled Card",
                    filterKey: conflict.key,
                    conflictingValues: conflict.conflictingValues,
                });
            });
        });

        return conflicts;
    }

    // Refresh methods - propagate refresh to all components
    @action
    refresh = async (filterUpdate?: ApplicationFilters) => {
        // cancelRefresh() clears pendingAppliedFiltersForRefresh, so we set it
        // from filterUpdate *after* this call.
        this.cancelRefresh();

        if (filterUpdate !== undefined) {
            // Keep the new candidate filters visible to components via
            // effectiveAppliedFilters while the eager refresh is running.
            this.pendingAppliedFiltersForRefresh = filterUpdate;
        }

        this.lazyRefreshGeneration += 1;
        const gen = this.lazyRefreshGeneration;

        // Allow components to run any refresh preparation before the batch starts
        this.components.forEach((component) => component.preRefresh?.());
        this.pendingRefreshComponents.clear();

        const queue = new AsyncQueue(MAX_CONCURRENT_REFRESHES);
        this.refreshQueue = queue;

        const orderedComponents = this.components.slice().sort((a, b) => {
            if (a.static !== b.static) {
                return a.static ? -1 : 1;
            }
            return (a.order ?? 0) - (b.order ?? 0);
        });

        if (orderedComponents.length === 0) {
            this.resolveInitialVisibilityIfPending();
        }

        // On initial load visibleComponentIds is empty until the IntersectionObserver fires.
        // Awaiting this promise ensures we know which components are visible before splitting.
        // On subsequent refreshes (filter changes) this resolves immediately.
        await this.initialVisibilityPromise;

        if (this.refreshQueue !== queue) {
            return;
        }

        // Split into eager (visible) and lazy (off-screen, enqueued on scroll).
        const eagerComponents: BaseComponentStore<any, any>[] = [];
        this.lazyPendingMap.clear();

        for (const component of orderedComponents) {
            if (this.visibleComponentIds.has(component.id)) {
                eagerComponents.push(component);
            } else {
                this.lazyPendingMap.set(component.id, component);
            }
        }

        this.pendingRefreshComponents = new Set(eagerComponents);
        eagerComponents.forEach((component) => {
            component.captureRefreshSnapshot();
            component.markRefreshQueued();
        });

        let componentsToRefresh = eagerComponents;
        let deferPass = 0;
        let forceStandaloneCompletion = false;

        while (componentsToRefresh.length > 0) {
            if (this.refreshQueue !== queue) {
                return;
            }

            const deferredById = new Map<
                string,
                {
                    component: BaseComponentStore<any, any>;
                    waitFor: Promise<void>;
                }
            >();

            const refreshTasks = componentsToRefresh.map((component) =>
                this.enqueueComponentRefresh(component, queue).then(
                    (refreshResult) => {
                        if (refreshResult?.status === "deferred") {
                            deferredById.set(component.id, {
                                component,
                                waitFor: refreshResult.waitFor,
                            });
                        }
                    },
                ),
            );

            await Promise.allSettled(refreshTasks);

            if (this.refreshQueue !== queue) {
                return;
            }

            if (!deferredById.size) {
                componentsToRefresh = [];
                break;
            }

            deferPass += 1;
            const deferredEntries = Array.from(deferredById.values());
            const deferredComponents = deferredEntries.map(
                (entry) => entry.component,
            );

            if (deferPass >= MAX_DEFER_REFRESH_PASSES) {
                componentsToRefresh = deferredComponents;
                forceStandaloneCompletion = true;
                break;
            }

            const waitResult = await this.waitForDeferredDependencies(
                queue,
                deferredEntries.map((entry) => entry.waitFor),
            );

            if (waitResult === "cancelled") {
                return;
            }

            if (waitResult === "timeout") {
                componentsToRefresh = deferredComponents;
                forceStandaloneCompletion = true;
                break;
            }

            componentsToRefresh = deferredComponents;
        }

        if (
            forceStandaloneCompletion &&
            componentsToRefresh.length > 0 &&
            this.refreshQueue === queue
        ) {
            const completionTasks = componentsToRefresh.map((component) =>
                this.enqueueComponentRefresh(component, queue, "standalone"),
            );

            await Promise.allSettled(completionTasks);
        }

        if (
            filterUpdate !== undefined &&
            this.refreshQueue === queue &&
            !this.components.some((component) => component.abortError)
        ) {
            this.lastAppliedFilters = _.cloneDeep(filterUpdate);
            this.pendingAppliedFiltersForRefresh = null;
        }

        // Commit if all eager + lazy components are done; lazy ones call this too from their finally
        this.commitRefreshIfComplete(gen, queue);
    };

    private async waitForDeferredDependencies(
        queue: AsyncQueue,
        waits: Promise<void>[],
    ): Promise<"ready" | "cancelled" | "timeout"> {
        let ready = false;
        void Promise.allSettled(waits).then(() => {
            ready = true;
        });

        const start = Date.now();
        while (true) {
            if (this.refreshQueue !== queue) {
                return "cancelled";
            }

            if (ready) {
                return "ready";
            }

            if (Date.now() - start >= DEFER_REFRESH_WAIT_TIMEOUT_MS) {
                return "timeout";
            }

            await new Promise<void>((resolve) =>
                setTimeout(resolve, DEFER_REFRESH_WAIT_POLL_MS),
            );
        }
    }

    @computed
    get isRefreshing() {
        // a dashboard is refreshing if ANY component is refreshing
        return this.components.some((c) => c.getTaskLoading("refresh"));
    }

    @computed
    get isAnySummaryLoading() {
        return this.components.some((c) => c.isSummaryLoading);
    }

    @observable private _exportChartTotal = 0;
    @observable private _exportSummaryTotal = 0;

    @computed
    get exportPercentage(): number {
        const chartsRemaining = this.components.filter((c) =>
            c.getTaskLoading("refresh"),
        ).length;
        const summariesRemaining = this.components.filter(
            (c) => c.isSummaryLoading,
        ).length;
        const chartsLoaded = Math.max(
            0,
            this._exportChartTotal - chartsRemaining,
        );
        const summariesLoaded = Math.max(
            0,
            this._exportSummaryTotal - summariesRemaining,
        );
        return Math.round(
            (this._exportChartTotal > 0
                ? (chartsLoaded / this._exportChartTotal) * 70
                : 70) +
            (this._exportSummaryTotal > 0
                ? (summariesLoaded / this._exportSummaryTotal) * 30
                : 0),
        );
    }

    @action
    cancelExport = () => {
        if (this.isExportCaptureInProgress) return;
        this._exportCancelled = true;
    };

    @action
    beginExportCapture = (): boolean => {
        if (this._exportCancelled) return false;

        this.isExportCaptureInProgress = true;
        return true;
    };

    @action
    endExportCapture = () => {
        this.isExportCaptureInProgress = false;
    };

    @computed
    get effectiveAppliedFilters() {
        return this.pendingAppliedFiltersForRefresh ?? this.lastAppliedFilters;
    }

    // Cancel refresh on all components
    @action
    cancelRefresh = () => {
        const hadActiveRefresh =
            !!this.refreshQueue ||
            this.pendingRefreshComponents.size > 0 ||
            this.isRefreshing;

        if (this.refreshQueue) {
            this.refreshQueue.cancelAll(new AbortError());
            this.refreshQueue = null;
        }

        if (hadActiveRefresh) {
            const componentsToReset = new Set(
                this.components.filter(
                    (component) =>
                        this.pendingRefreshComponents.has(component) ||
                        component.getTaskLoading("refresh"),
                ),
            );

            componentsToReset.forEach((component) => {
                // Reset any in-flight or queued component back to its snapshot and flag the abort
                component.markRefreshCancelled();
            });
        }
        this.pendingRefreshComponents.clear();

        this.components.forEach((component) => {
            component.cancelRefresh();
        });

        // Invalidate any in-flight lazy enqueue callbacks
        this.lazyRefreshGeneration += 1;
        this.lazyPendingMap.clear();

        this.pendingAppliedFiltersForRefresh = null;
    };

    @action
    notifyComponentsVisible = (componentIds: string[]) => {
        this.resolveInitialVisibilityIfPending();
        componentIds.forEach((id) => this.visibleComponentIds.add(id));
        this.dequeueLazyComponents(componentIds);
    };

    @action
    notifyComponentsHidden = (componentIds: string[]) => {
        this.resolveInitialVisibilityIfPending();
        componentIds.forEach((id) => this.visibleComponentIds.delete(id));
    };

    // Forces all lazy-pending components to refresh immediately, then resolves
    // when the dashboard is no longer refreshing. Used by PDF export so the
    // capture sees fully-refreshed data for every card, not just visible ones.
    // Returns false if the user cancelled, in which case the caller should not
    // proceed with the PDF capture.
    loadAllComponentsForExport = async (): Promise<boolean> => {
        runInAction(() => {
            this._exportCancelled = false;
            this._exportChartTotal = 0;
            this._exportSummaryTotal = 0;
        });

        if (this._exportCancelled) return false;

        const pendingIds = Array.from(this.lazyPendingMap.keys());
        if (pendingIds.length > 0) {
            this.dequeueLazyComponents(pendingIds);
        }

        runInAction(() => {
            this._exportChartTotal = this.components.filter((c) =>
                c.getTaskLoading("refresh"),
            ).length;
        });

        const areChartDependenciesLoading = () =>
            this.charts.some(
                (chart) =>
                    chart.comparePrevLoading ||
                    chart.isWaitingOnRequiredOptions ||
                    (chart.chartType === "TreeMap" &&
                        chart.zoomStore.isFetching),
            );

        const shouldContinue = await this.waitForExportStage(
            () =>
                (!this.isRefreshing && !areChartDependenciesLoading()) ||
                this._exportCancelled,
        );
        if (!shouldContinue) return false;

        // Google Charts reports data loading complete before its SVG animation
        // finishes. Wait past the longest current animation (1 second).
        await new Promise((resolve) =>
            setTimeout(resolve, EXPORT_CHART_SETTLE_MS),
        );

        return !this._exportCancelled;
    };

    // Called from exportToPDF after a rAF has given React time to start AI summary
    // effects post-render. Snapshots the summary count then waits for them to finish.
    waitForSummariesAfterExport = async (): Promise<boolean> => {
        runInAction(() => {
            this._exportSummaryTotal = this.components.filter(
                (c) => c.isSummaryLoading,
            ).length;
        });

        if (this.isAnySummaryLoading) {
            const shouldContinue = await this.waitForExportStage(
                () => !this.isAnySummaryLoading || this._exportCancelled,
            );
            if (!shouldContinue) return false;
        }

        return !this._exportCancelled;
    };

    private async waitForExportStage(
        predicate: () => boolean,
    ): Promise<boolean> {
        try {
            await when(predicate, { timeout: EXPORT_STAGE_TIMEOUT_MS });
        } catch {
            // Stop waiting after the timeout and export the current state.
        }

        return !this._exportCancelled;
    }

    private dequeueLazyComponents(ids: string[]) {
        if (!this.refreshQueue) return;
        const gen = this.lazyRefreshGeneration;
        const queue = this.refreshQueue;

        for (const id of ids) {
            const component = this.lazyPendingMap.get(id);
            if (!component) continue;

            // Wrap synchronous observable mutations in runInAction so they are safe
            // to call from non-@action contexts (e.g. loadAllComponentsForExport).
            runInAction(() => {
                this.lazyPendingMap.delete(id);
                this.pendingRefreshComponents.add(component);
                component.captureRefreshSnapshot();
                component.markRefreshQueued();
            });

            // Pass removePendingOnStart=false so enqueueComponentRefresh never
            // removes the component from pendingRefreshComponents mid-chain. This
            // closes the microtask race where a concurrent component's .finally()
            // could call commitRefreshIfComplete while this one is between its
            // queue-task completion and its .then() deferred-re-add. The outer
            // .finally() below is the sole place the component is removed.
            void this.enqueueComponentRefresh(component, queue, "dashboard-batch", false)
                .then(async (refreshResult) => {
                    if (refreshResult?.status !== "deferred") {
                        return;
                    }

                    try {
                        const waitResult =
                            await this.waitForDeferredDependencies(queue, [
                                refreshResult.waitFor,
                            ]);

                        if (
                            waitResult === "cancelled" ||
                            this.lazyRefreshGeneration !== gen ||
                            this.refreshQueue !== queue
                        ) {
                            return;
                        }

                        await this.enqueueComponentRefresh(
                            component,
                            queue,
                            "standalone",
                            false,
                        );
                    } finally {
                        runInAction(() => {
                            // Deferred charts skip setupAsyncTask, so the loading flag
                            // set by markRefreshQueued is never cleared by the normal
                            // completion path. Clear it here if it's still set.
                            if (component.getTaskLoading("refresh")) {
                                component.markRefreshCancelled();
                            }
                        });
                    }
                })
                .finally(() => {
                    // Single authoritative removal — avoids the microtask gap that existed
                    // when removal happened inside enqueueComponentRefresh's queue task.
                    runInAction(() =>
                        this.pendingRefreshComponents.delete(component),
                    );
                    if (this.lazyRefreshGeneration === gen) {
                        runInAction(() =>
                            this.commitRefreshIfComplete(gen, queue),
                        );
                    }
                })
                .catch((error) => {
                    console.error(
                        `Lazy component refresh failed for component ${component.id}:`,
                        error,
                    );
                });
        }
    }

    private enqueueComponentRefresh(
        component: BaseComponentStore<any, any>,
        queue: AsyncQueue,
        origin: "dashboard-batch" | "standalone" = "dashboard-batch",
        // Pass false when the caller manages pendingRefreshComponents externally
        // (e.g. dequeueLazyComponents) to prevent a gap where the component is
        // absent from the set between the queue-task start and the .then() re-add.
        removePendingOnStart: boolean = true,
    ): Promise<RefreshResult | undefined> {
        let started = false;

        return queue
            .enqueue(async () => {
                started = true;
                if (removePendingOnStart) {
                    this.pendingRefreshComponents.delete(component);
                }
                return await component.runRefresh(false, { origin });
            })
            .catch((error) => {
                if (error instanceof AbortError) {
                    return undefined;
                }
                throw error;
            })
            .finally(() => {
                if (!started) {
                    component.markRefreshCancelled();
                    this.pendingRefreshComponents.delete(component);
                }
            });
    }

    private resolveInitialVisibilityIfPending() {
        if (this.resolveInitialVisibility) {
            this.resolveInitialVisibility();
            this.resolveInitialVisibility = null;
        }
    }

    private commitRefreshIfComplete(gen: number, queue: AsyncQueue) {
        if (
            this.lazyRefreshGeneration === gen &&
            this.refreshQueue === queue &&
            this.lazyPendingMap.size === 0 &&
            this.pendingRefreshComponents.size === 0
        ) {
            this.components.forEach((c) => c.commitRefreshSnapshot());
            this.refreshQueue = null;
            // pendingRefreshComponents is already empty here — both eager and lazy
            // components remove themselves as they complete.

            this.pendingAppliedFiltersForRefresh = null;
        }
    }

    // Set isHome methods

    @action setIsUserHomeDashboard = (val: boolean) => {
        this.isUserHomeDashboard = val;
    };

    @action setIsOrgHomeDashboard = (val: boolean) => {
        this.isOrgHomeDashboard = val;
    };

    @action setIsPresentationMode = (val: boolean) => {
        this.isPresentationMode = val;
        // Components snapshot this at construction, so without fanning the toggle out they stay
        // on whatever the dashboard's mode was when they were built — which for a normally loaded
        // dashboard is always false.
        this.components.forEach((component) =>
            component.setIsPresentationMode(val),
        );
    };

    @action setIsSelected = (val: boolean) => {
        this.isSelected = val;
    };

    // Gets the "route" for the dashboard based on its type
    // Examples: ThemesAndDrivers -> /drivers, Custom -> /dashboard, Eddy -> /eddy
    @computed
    get route() {
        return DashboardTypeRoutes.get(this.dashboardType);
    }

    // Signals Objects

    @action
    deleteSignalsObjectById = async (objectId: string) => {
        if (this.refuseCardChangeWhileArranging()) return;

        const componentToRemove = this.components.find(
            (c) => c.id === objectId,
        );
        if (!componentToRemove) {
            this.messageStore.logError("Failed to find component to delete.");
            return;
        }

        // Snapshot for rollback
        const previousComponents = this.components.slice();
        const previousSignalsObjects = this.signalsObjects.slice();
        const previousChartCount = this.signalsChartCount;
        const restorePlacement = this.dashboardLayoutStore.rememberPlacement();
        const restoreDeletedState = (includeChartCount: boolean = false) => {
            this.components = previousComponents;
            this.signalsObjects = previousSignalsObjects;
            restorePlacement();
            if (includeChartCount) {
                this.signalsChartCount = previousChartCount;
            }
        };

        // Optimistic update. The server derives the remaining cards' order.
        this.components = this.components.filter((c) => c.id !== objectId);
        this.signalsObjects = this.signalsObjects.filter(
            (so) => so.id !== objectId,
        );

        try {
            switch (componentToRemove.objectType) {
                case SignalsObjectType.Chart:
                    await this.signalsService.deleteSignalsChart({
                        id: objectId,
                    });
                    this.signalsChartCount = Math.max(
                        0,
                        this.signalsChartCount - 1,
                    );
                    break;
                case SignalsObjectType.Table:
                    await this.signalsService.deleteSignalsTableCard({
                        id: objectId,
                    });
                    this.signalsChartCount = Math.max(
                        0,
                        this.signalsChartCount - 1,
                    );
                    break;
                case SignalsObjectType.Unique:
                    await this.signalsService.deleteSignalsUniqueCard({
                        id: objectId,
                    });
                    this.signalsChartCount = Math.max(
                        0,
                        this.signalsChartCount - 1,
                    );
                    break;
                case SignalsObjectType.SmallCard:
                    await this.signalsService.deleteSignalsSmallCard({
                        id: objectId,
                    });
                    this.messageStore.logMessage(
                        "Small card deleted",
                        "success",
                    );
                    break;
                case SignalsObjectType.TextCard:
                    await this.signalsService.deleteSignalsTextCard({
                        id: objectId,
                    });
                    break;
                case SignalsObjectType.MontageCard:
                    await this.signalsService.deleteSignalsMontageCard({
                        id: objectId,
                    });
                    break;
                default:
                    runInAction(() => {
                        restoreDeletedState();
                    });
                    this.messageStore.logError(
                        "Deleting this type of component is not yet supported.",
                    );
                    return;
            }
        } catch (e) {
            runInAction(() => {
                restoreDeletedState(true);
            });
            this.messageStore.logError("Failed to delete card");
            throw e;
        }

        await this.syncFromServer();
    };

    /** Saves a text card's text, leaving its position alone. */
    @action
    updateTextCardText = async (objectId: string, cardText: string) => {
        const target = this.signalsObjects.find((obj) => obj.id === objectId);
        if (!target || target.objectType !== SignalsObjectType.TextCard) {
            this.messageStore.logError("Failed to update text card");
            return;
        }

        const previousText = (target as ISignalsTextCard).cardText;
        this.signalsObjects = this.signalsObjects.map((obj) =>
            obj.id === objectId ? { ...obj, cardText } : obj,
        );

        try {
            await this.signalsService.updateSignalsTextCard(
                target.id,
                cardText,
            );
        } catch (e) {
            this.signalsObjects = this.signalsObjects.map((obj) =>
                obj.id === objectId
                    ? { ...obj, cardText: previousText }
                    : obj,
            );
            this.messageStore.logError("Failed to update text card");
            throw e;
        }
    };

    /**
     * Adds cards through the server, which places them at the bottom of the
     * layout, then brings this dashboard into step with it. The cards the
     * server created show straight away, so they are on the page even if that
     * sync fails, and this resolves without waiting for the sync: callers
     * confirm the add and close their dialogs while it runs. Resolves false
     * without adding anything while the layout is being arranged.
     */
    addCards = async (
        selectedCards: SelectedCardRequestPayload[],
    ): Promise<boolean> => {
        if (this.refuseCardChangeWhileArranging()) return false;

        const createdCards =
            await this.signalsService.addSelectedCardsToDashboard(
                this.id,
                selectedCards,
            );
        this.showCreatedCards(createdCards ?? []);
        void this.syncFromServer();
        return true;
    };

    /**
     * Shows cards the server just created, at the bottom until the layout
     * arrives. A card already shown is left alone.
     */
    @action
    private showCreatedCards(createdCards: RenderedSignalsObject[]) {
        const shownIds = new Set(this.signalsObjects.map((obj) => obj.id));
        const newCards = createdCards.filter((card) => !shownIds.has(card.id));
        if (newCards.length === 0) return;

        this.signalsObjects = [...this.signalsObjects, ...newCards];
        this.components = [
            ...this.components,
            ...newCards.map((card) => this.createComponentStore(card)),
        ];
        this.signalsChartCount += newCards.filter((card) =>
            countsTowardChartLimit(card.objectType),
        ).length;

        if (!this.isSelected) {
            // Cards added to an inactive dashboard may skip their own refresh.
            // Clear the applied snapshot so navigating back refreshes them.
            this.lastAppliedFilters = null;
        }
    }

    /** Saves a montage card's title, leaving its position alone. */
    @action
    updateMontageCardTitle = async (objectId: string, title: string) => {
        const component = this.components.find((c) => c.id === objectId);
        const target = this.signalsObjects.find((obj) => obj.id === objectId);
        if (
            !component ||
            !target ||
            target.objectType !== SignalsObjectType.MontageCard
        ) {
            this.messageStore.logError("Failed to update card");
            return;
        }

        const previousTitle = component.title;
        const setTitle = (nextTitle: string | undefined) => {
            component.title = nextTitle;
            this.signalsObjects = this.signalsObjects.map((obj) =>
                obj.id === objectId ? { ...obj, title: nextTitle } : obj,
            );
        };
        setTitle(title);

        try {
            await this.signalsService.updateSignalsMontageCard(objectId, title);
            this.messageStore.logMessage("Card updated", "success");
        } catch (e) {
            runInAction(() => setTitle(previousTitle));
            this.messageStore.logError("Failed to update card");
            throw e;
        }
    };

    // Add the selected cards from the card selector to the dashboard
    @action
    addSelectedCardsToDashboard = async () => {
        this.setupAsyncTask("AddCards", async () => {
            try {
                const selectedCards =
                    this.signalsCardSelectorStore.buildSelectedCardsPayload();
                if (selectedCards.length === 0) {
                    this.messageStore.logError(
                        "Select at least one card to add to the dashboard.",
                    );
                    return;
                }

                if (!(await this.addCards(selectedCards))) return;

                this.messageStore.logMessage(
                    "Added card(s) to dashboard.",
                    "success",
                );
                this.signalsCardSelectorStore.showReportSelector = false;
            } catch (e) {
                if (e instanceof ServiceError && e.errorMessage) {
                    this.messageStore.logError(e.errorMessage);
                } else {
                    this.messageStore.logMessage(
                        "Failed to add card(s) to dashboard.",
                        "error",
                    );
                }
            } finally {
                this.signalsCardSelectorStore.resetSelections();
            }
        });
    };
}

export default DashboardStore;
